import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { useSession } from '../lib/auth'
import type { RoleUtilisateur } from '../lib/api/types'
import { libelleRole } from '../lib/roles'
import { Squelette } from './ui/Squelette'

interface Props {
  children: ReactNode
  roles?: RoleUtilisateur[]
}

/**
 * Garde de route appliquant la matrice R6.
 *
 * **Trois comportements distincts**, et les confondre est une faute
 * d'ergonomie autant que de sécurité :
 *
 *   non authentifié       → `/connexion`, avec l'URL d'origine conservée
 *   rôle insuffisant      → page 403 **explicite**, jamais une redirection
 *                           silencieuse : renvoyer quelqu'un à l'accueil sans
 *                           un mot lui fait croire à un bug
 *   mot de passe à changer → redirection forcée, aucune autre route accessible
 */
export function RouteProtegee({ children, roles }: Props) {
  const { utilisateur, chargement } = useSession()
  const emplacement = useLocation()

  if (chargement) {
    return (
      <div className="p-8">
        <Squelette lignes={6} />
      </div>
    )
  }

  if (!utilisateur) {
    const destination = `${emplacement.pathname}${emplacement.search}`
    return <Navigate to={`/connexion?suite=${encodeURIComponent(destination)}`} replace />
  }

  if (utilisateur.doit_changer_mot_de_passe && emplacement.pathname !== '/mot-de-passe/changer') {
    return <Navigate to="/mot-de-passe/changer" replace />
  }

  if (roles && !roles.includes(utilisateur.role)) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center" role="alert">
        <p className="text-5xl" aria-hidden="true">
          🔒
        </p>
        <h1 className="mt-4 text-2xl font-semibold text-slate-900">Accès refusé</h1>
        <p className="mt-2 text-slate-600">
          {roles.length === 1 && roles[0] === 'TITULAIRE'
            ? 'Cette page est réservée aux titulaires du cabinet.'
            : 'Votre rôle ne permet pas d’accéder à cette page.'}
        </p>
        <p className="mt-1 text-sm text-slate-500">
          Vous êtes connecté en tant que {libelleRole(utilisateur.role)}.
        </p>
      </div>
    )
  }

  return <>{children}</>
}

